import Toast from 'app/utils/Toast';
import CloseBlackIcon from 'assets/images/incorrect.png';
import { useEffect, useRef, useState } from 'react';
import './index.scss';

import { IoInformationCircleOutline } from 'react-icons/io5';
import { showImageUrl } from 'app/utils/Parsers';

const NewAttachment = ({
  onFileSelected,
  value = null,
  id,
  label,
  customoparentclassName = '',
  customclassName = '',
  sizevalidationUpto = 1, // sizevalidationUpto is in MB
  videoSizevalidationUpto = 3,
  asterisk = false,
  alt = '',
  errorText = '',
  videoAllowed = false,
  // Opt-in PDF support, mirroring videoAllowed. Defaults false so every
  // existing consumer keeps rejecting documents exactly as before.
  documentAllowed = false,
  dimesnsionLabel = '',
  isDisabled = false,
}) => {
  const fileRef = useRef();
  const [selFile, setSelFile] = useState(null);
  const [isImage, setIsImage] = useState(false);
  const [isDocument, setIsDocument] = useState(false);
  const [isValidFile, setIsValidFile] = useState(false);
  const [isRemoteUrl, setIsRemoteUrl] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);

  useEffect(() => {
    if (selFile instanceof File) {
      const objectUrl = URL.createObjectURL(selFile);
      setLocalPreviewUrl(objectUrl);

      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }

    setLocalPreviewUrl(null);
    return undefined;
  }, [selFile]);

  useEffect(() => {
    if (value) {
      const isRemote =
        value.startsWith('http') || value.startsWith('https') ? true : false;
      setIsRemoteUrl(isRemote);
      setSelFile(value);

      const extension = value.split('?')[0].split('.').pop().toLowerCase();
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
      const videoExtensions = ['mp4'];

      if (imageExtensions.includes(extension)) {
        setIsImage(true);
        setIsDocument(false);
        setIsValidFile(true);
      } else if (videoExtensions.includes(extension)) {
        setIsImage(false);
        setIsDocument(false);
        setIsValidFile(true);
      } else if (documentAllowed && extension === 'pdf') {
        setIsImage(false);
        setIsDocument(true);
        setIsValidFile(true);
      } else {
        setIsValidFile(false); // Set invalid if the file extension is not valid
      }
    } else {
      setSelFile(null);
    }
  }, [value, documentAllowed]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Define the allowed file types and extensions
    const validFileTypes = videoAllowed
      ? ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'video/mp4']
      : ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];

    const validExtensions = videoAllowed
      ? ['png', 'jpeg', 'jpg', 'gif', 'mp4']
      : ['png', 'jpeg', 'jpg', 'gif', 'webp'];

    if (documentAllowed) {
      validFileTypes.push('application/pdf');
      validExtensions.push('pdf');
    }

    // Extract file extension and MIME type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const isValidType = validFileTypes.includes(file.type);
    const isValidExtension = validExtensions.includes(fileExtension);

    // Validate both file type and extension
    if (!isValidType || !isValidExtension) {
      setIsValidFile(false); // Invalid file type
      Toast.error(
        'Invalid file type. Please select a valid image or video file.'
      );
      return;
    }

    // Check if video upload is allowed
    if (file.type === 'video/mp4' && !videoAllowed) {
      setIsValidFile(false); // Video not allowed
      Toast.error('Video upload is not allowed.');
      return;
    }

    // File size validation
    const sizeLimit =
      file.type === 'video/mp4'
        ? videoSizevalidationUpto * 1024 * 1024
        : sizevalidationUpto * 1024 * 1024;

    if (file.size > sizeLimit) {
      const sizeLimitMB =
        file.type === 'video/mp4'
          ? videoSizevalidationUpto
          : sizevalidationUpto;
      setIsValidFile(false); // Invalid file size
      Toast.error(`Please select a file up to ${sizeLimitMB} MB.`);
      return;
    }

    // File is valid, update states
    setSelFile(file);
    setIsImage(file.type.startsWith('image'));
    setIsDocument(file.type === 'application/pdf');
    setIsValidFile(true);
    setIsRemoteUrl(false);

    // Pass the file to the parent component if the callback is provided
    onFileSelected && onFileSelected(file, id, file.type.startsWith('image'));

    // Reset file input for subsequent uploads
    fileRef.current.value = null;
  };

  const handleRemove = (event) => {
    event.stopPropagation();
    setSelFile(null); // Clear selected file
    setIsValidFile(true); // Reset validation state
    setIsRemoteUrl(false); // Clear remote URL state

    // Notify parent of file removal
    onFileSelected && onFileSelected(null, id, isImage);

    // Reset file input field
    if (fileRef.current) {
      fileRef.current.value = null;
    }
  };

  const handleClick = () => {
    if (!selFile && !isDisabled) {
      if (fileRef.current) {
        fileRef.current.click();
      }
    }
  };

  const RenderFile = ({ children }) => (
    <div
      className={`${
        isImage
          ? `newattachment_img_wrapper ${customoparentclassName}`
          : isDocument
          ? `newattachment_img_wrapper ${customoparentclassName}`
          : 'newattachment_img_wrapper video'
      }`}
    >
      {children}
      <div className='newattachment_img_wrapper_close'>
        <img
          crossOrigin='anonymous'
          src={CloseBlackIcon}
          alt='Remove'
          onClick={(e) => handleRemove(e)}
        />
      </div>
    </div>
  );

  return (
    <>
      <div className='newattachment_container'>
        {!selFile && (
          <>
            <div
              className={`newattachment ${isDisabled ? 'disabled' : ''}`}
              onClick={handleClick}
            >
              <div className='newattachment_text flex'>
                <span className='label-btn'>
                  {label || 'Add an attachment'}
                </span>
                <span className='newjpgsizelabel'>
                  {documentAllowed
                    ? 'Upload document or image here'
                    : 'Upload Image file here'}
                </span>
                {asterisk && <span className='asterisk'>*</span>}
              </div>
              <input
                ref={fileRef}
                type='file'
                id={id || 'file'}
                accept={`${
                  videoAllowed
                    ? '.jpg, .jpeg, .png, .gif, .mp4'
                    : '.jpg, .jpeg, .png, .gif, .webp'
                }${documentAllowed ? ', .pdf' : ''}`}
                className='newattachment__input-file'
                disabled={isDisabled}
                onChange={async (event) => {
                  setSelFile(null);
                  setIsValidFile(false);
                  setIsImage(false);
                  setIsRemoteUrl(false);

                  await handleFileChange(event);
                  event.target.value = '';
                }}
              />
            </div>
            <div className='info-text'>
              <label>
                {documentAllowed
                  ? `File Format: (PDF/JPG/JPEG/PNG) up to ${sizevalidationUpto} MB`
                  : videoAllowed
                  ? `File Format: (JPG/JPEG/PNG/MP4) up to ${sizevalidationUpto} MB ${
                      dimesnsionLabel && `& Resolution:${dimesnsionLabel}`
                    }`
                  : `File Format: (JPG/JPEG/PNG/WEBP) up to ${sizevalidationUpto} MB  ${
                      dimesnsionLabel && `&  Resolution:${dimesnsionLabel}`
                    } `}

                <IoInformationCircleOutline className='info-text-image' />
              </label>
            </div>
          </>
        )}

        {selFile && isValidFile && (
          <RenderFile>
            {isDocument ? (
              // PDFs have no thumbnail — link out instead of rendering a
              // broken <img>. A freshly picked File uses its object URL; a
              // stored value is already a presigned URL from the server.
              <a
                className={`attachment-media attachment-document ${customclassName}`}
                href={
                  selFile instanceof File ? localPreviewUrl : showImageUrl(selFile)
                }
                target='_blank'
                rel='noopener noreferrer'
                onClick={(e) => e.stopPropagation()}
              >
                {selFile instanceof File ? selFile.name : 'View document'}
              </a>
            ) : isImage ? (
              selFile instanceof File ? (
                <img
                  src={localPreviewUrl}
                  alt={alt ? alt : 'No image'}
                  className={`attachment-media ${customclassName}`}
                />
              ) : (
                // Stored string value (relative or absolute): showImageUrl
                // returns http(s) URLs unchanged and resolves a relative
                // path (e.g. an S3 key) against REACT_APP_S3_BUCKET_URL, so
                // both cases render a real preview instead of a broken
                // image icon (quick task 260717-fhf).
                <img
                  src={showImageUrl(selFile)}
                  alt={alt ? alt : 'No image'}
                  className={`attachment-media ${customclassName}`}
                  crossOrigin='anonymous'
                />
              )
            ) : (
              <video className={`attachment-media ${customclassName}`} controls>
                <source
                  crossOrigin='anonymous'
                  src={
                    selFile instanceof File
                      ? localPreviewUrl
                      : showImageUrl(selFile)
                  }
                  type='video/mp4'
                />
              </video>
            )}
          </RenderFile>
        )}

        {selFile && !isValidFile && (
          <div className='error-message'>Invalid file format.</div>
        )}
      </div>
      {errorText && <p className='loginWrapper-valid-text'>{errorText}</p>}
    </>
  );
};

export default NewAttachment;
